/**
 * mapsService.js
 * All Google Maps calls go through our backend proxy (/api/v1/maps/*)
 * This avoids CORS issues — Google's APIs don't allow direct browser calls.
 */

const API = process.env.REACT_APP_API_URL;

// ── Places Autocomplete ───────────────────────────────────────────────────────
export async function getPlaceSuggestions(input) {
  if (!input?.trim() || input.length < 3) return [];
  try {
    const res  = await fetch(`${API}/maps/autocomplete?input=${encodeURIComponent(input)}`);
    const data = await res.json();
    if (data.status !== "OK" && data.status !== "ZERO_RESULTS") return [];
    return (data.predictions || []).map(p => ({
      place_id:    p.place_id,
      description: p.description.replace(", Ghana", "").replace(", GH", ""),
      main_text:   p.structured_formatting?.main_text || p.description,
    }));
  } catch {
    return [];
  }
}

// ── Geocode a text address → { lat, lng, formatted_address } ─────────────────
export async function geocodeAddress(address) {
  if (!address?.trim()) throw new Error("Address is empty");
  const res  = await fetch(`${API}/maps/geocode?address=${encodeURIComponent(address)}`);
  const data = await res.json();
  if (data.status === "ZERO_RESULTS") throw new Error(`Address not found: "${address}"`);
  if (data.status !== "OK") throw new Error(`Geocoding error: ${data.status}`);
  const { lat, lng } = data.results[0].geometry.location;
  return { lat, lng, formatted_address: data.results[0].formatted_address };
}

// ── Road distance between two lat/lng pairs ───────────────────────────────────
export async function getRoadDistance(originLat, originLng, destLat, destLng) {
  const origins      = `${originLat},${originLng}`;
  const destinations = `${destLat},${destLng}`;
  const res  = await fetch(`${API}/maps/distance?origins=${encodeURIComponent(origins)}&destinations=${encodeURIComponent(destinations)}`);
  const data = await res.json();
  if (data.status !== "OK") throw new Error(`Distance Matrix error: ${data.status}`);
  const element = data.rows[0]?.elements[0];
  if (!element || element.status !== "OK") throw new Error("Could not calculate route between these addresses");
  return {
    distanceKm:   element.distance.value / 1000,
    durationMins: Math.ceil(element.duration.value / 60),
    distanceText: element.distance.text,
    durationText: element.duration.text,
  };
}

// ── Combined: geocode both + road distance + price ────────────────────────────
export async function resolveDelivery(pickupAddress, dropoffAddress) {
  const [pickup, dropoff] = await Promise.all([
    geocodeAddress(pickupAddress),
    geocodeAddress(dropoffAddress),
  ]);
  const { distanceKm, durationMins, distanceText, durationText } = await getRoadDistance(
    pickup.lat, pickup.lng, dropoff.lat, dropoff.lng
  );
  const price = (5 + distanceKm * 1.5).toFixed(2);
  return {
    pickup:  { lat: pickup.lat,  lng: pickup.lng,  formatted: pickup.formatted_address },
    dropoff: { lat: dropoff.lat, lng: dropoff.lng, formatted: dropoff.formatted_address },
    distanceKm:   parseFloat(distanceKm.toFixed(2)),
    distanceText,
    durationMins,
    durationText,
    price,
  };
}
